import { useEffect, useMemo, useState } from "react";

import {
  type FetchUserAttributesOutput,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from "aws-amplify/auth";
import { isUndefined, throttle } from "lodash";

import { useGetCurrentUserQuery } from "@generated/graphql";

import { extractOktaGroups } from "@common/authentication";

import type { AuthenticationContext } from "./authentication";

const captureUserAttributes = (userAttributes: FetchUserAttributesOutput) => {
  const rawAttributes = Object.entries(userAttributes).filter((e): e is [string, string] => !isUndefined(e[1]));
  return Object.fromEntries(rawAttributes);
};

const getUserGroups = async (
  userAttributes: FetchUserAttributesOutput,
  shouldForceRefresh?: boolean,
): Promise<string[]> => {
  let groups: string[] = [];

  const fetchCondition = shouldForceRefresh ? { forceRefresh: true } : {};
  const session = await fetchAuthSession({ ...fetchCondition });
  const userGroups = session.tokens?.accessToken.payload["cognito:groups"];

  if (Array.isArray(userGroups)) {
    groups = groups.concat(userGroups as string[]);
  }

  const oktaGroups = extractOktaGroups(captureUserAttributes(userAttributes));

  return groups.concat(oktaGroups);
};

const getUserAttributes = throttle(fetchUserAttributes, 10);

export const useGetAuthContext = (
  isAuthenticated: boolean,
  forceRefresh: boolean = false,
  isinternalUser: boolean = false,
) => {
  // Skip the query if neither isAuthenticated nor forceRefresh is true and the user is an internal user
  const shouldFetch = useMemo(
    () => (isAuthenticated || forceRefresh) && !isinternalUser,
    [isAuthenticated, forceRefresh, isinternalUser],
  );

  const { data, loading, error, refetch } = useGetCurrentUserQuery({
    skip: !shouldFetch,
  });
  const [authContext, setAuthContext] = useState<AuthenticationContext | undefined>(undefined);

  useEffect(() => {
    const fetchAuthContext = async () => {
      if (loading || error || !data || !isAuthenticated || isinternalUser) {
        return;
      }

      try {
        if (shouldFetch) {
          refetch();
        }
        const authenticatedUserData = await getCurrentUser();

        const authenticatedUserAttributes = (await getUserAttributes()) || {};
        const userGroups = await getUserGroups(authenticatedUserAttributes, forceRefresh);

        setAuthContext({
          ...authenticatedUserData,
          userDetails: authenticatedUserAttributes,
          userGroups,
          currentUserData: data.currentUserData,
        });
      } catch (err) {
        console.error("Error fetching auth context:", err);
      }
    };

    fetchAuthContext();
  }, [loading, error, data, isAuthenticated, forceRefresh, shouldFetch, refetch, isinternalUser]);

  return { authContext, loading, error, refetch };
};
